import { DashboardRoutes } from "@/router";
import { useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { GuaranteeType } from "@payping/api/app/api/bnplMerchantTypes";
import type { PlanInfo } from "@payping/api/app/api/bnplMerchantTypes";
import { Close, CuToman, FiPlus, InfoCircleFilled } from "@payping/icons";
import {
  Button,
  Col,
  Copy,
  Drawer,
  Empty,
  Flex,
  LegacyCard,
  Modal,
  Row,
  Tabs,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { ComponentProps, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditSalaryDeduction from "./EditSalaryDeduction";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

enum PlanListStatus {
  active = "1",
  deactive = "0",
}

const BnplManagePlanList = () => {
  const { t } = useAppTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [planCode] = useState<string>();
  const [activeKey, setActiveKey] = useState<string>(PlanListStatus.active);
  const [planList, setPlanList] = useState<Array<PlanInfo>>();
  const [activePlan, setActivePlan] = useState<PlanInfo>({});

  const { breakpoints } = usePaypingContext();
  const { xs } = breakpoints;
  const navigate = useNavigate();

  const itemsList: ComponentProps<typeof Tabs>["items"] = [
    {
      label: t("common.active"),
      key: PlanListStatus.active,
    },
    {
      label: t("common.inactive"),
      key: PlanListStatus.deactive,
    },
  ];

  const createPlan = () => {
    navigate(DashboardRoutes.myShop.bnpl.plans.addPlan.root.url);
  };

  const [{ loading }, getList] = useMessagedAsyncFn(
    async () => {
      const res = await bnplMerchantApis.planList();
      setPlanList([...res.plansInfo]);
      return res;
    },
    [],
    undefined,
    { success: false },
  );

  const [{ loading: activeLoading }, toggleActive] = useMessagedAsyncFn(
    async (plan: PlanInfo) => {
      const res = await bnplMerchantApis.togglePlanActive({
        planCode: String(plan?.code),
        isActive: !plan?.isActive,
      });
      await getList();
      setOpenModal(false);
      return res;
    },
    [getList],
    undefined,
  );

  useEffect(() => {
    getList();
  }, [getList]);

  const openActivationModal = (item: PlanInfo) => {
    if (item.isActive) {
      setOpenModal(true);
      setActivePlan({ ...item });
    } else {
      toggleActive(item);
    }
  };

  return (
    <>
      <Flex vertical className="tw:p-5!">
        <Typography.Title level={5} className="tw:m-0!">
          {t("shop.bnpl.addInstallmentPlan.PlanList.title")}
        </Typography.Title>
        <Typography.Text className="tw:mt-4! tw:text-color-text-secondary">
          {t("shop.bnpl.addInstallmentPlan.PlanList.description")}
        </Typography.Text>
        <Flex gap={"middle"} className="tw:mt-5!" vertical={xs}>
          <Button block={false} onClick={createPlan} className="tw:px-8!" loading={false} type="primary">
            <FiPlus width={16} className="tw:ml-1!" />
            {t("shop.bnpl.addInstallmentPlan.PlanList.addPlan")}
          </Button>
          {/* <Button block={false} className="tw:px-8!" loading={false} type="default">
            {t("shop.bnpl.addInstallmentPlan.PlanList.previewPlans")}
          </Button> */}
        </Flex>
        <Tabs
          className="tw:mt-5!"
          onChange={(key) => setActiveKey(key)}
          style={{ border: 0 }}
          items={itemsList}
          activeKey={activeKey}
        />

        {planList?.filter((a) => Number(a.isActive) === Number(PlanListStatus.active)).length === 0 &&
        !loading &&
        Number(activeKey) !== 0 ? (
          <Flex vertical align="center" justify="center" className="tw:mt-40!">
            <Empty
              className="tw:font-bold"
              description={
                <span className="tw:text-color-text">{t("shop.bnpl.addInstallmentPlan.PlanList.createPlan")}</span>
              }
            />
          </Flex>
        ) : null}

        <Row gutter={[24, 24]}>
          {planList
            ?.filter((a) => Number(a.isActive) === Number(activeKey))
            .map((item, index) => (
              <Col xs={24} sm={12} key={index}>
                <LegacyCard.Ant className="tw:p-0!">
                  <div className="tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-1 tw:border-0 tw:border-b tw:border-solid tw:border-color-bg-layout tw:pb-3!">
                    <Typography.Title level={3} className="tw:m-0!">
                      {t("common.until")}
                    </Typography.Title>

                    <Flex align="center">
                      <Typography.Title
                        level={3}
                        className="tw:m-0!"
                      >{`${formatLocalizedNumber({ value: item?.totalMaxAmount ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </div>
                  <Flex
                    vertical
                    gap={"small"}
                    className="tw:mt-2! tw:border-0 tw:border-b tw:border-solid tw:border-color-bg-layout tw:pb-3!"
                  >
                    <Flex justify="space-between">
                      <div className="tw:text-color-text-secondary">
                        {t("shop.bnpl.addInstallmentPlan.PlanList.prepayment")}
                      </div>
                      <div className="tw:font-bold tw:text-color-text-secondary"> {item.prepaymentRate}٪</div>
                    </Flex>
                    <Flex justify="space-between">
                      <div className="tw:text-color-text-secondary">
                        {t("shop.bnpl.addInstallmentPlan.PlanList.installmentCount")}
                      </div>
                      <div className="tw:font-bold tw:text-color-text-secondary">
                        {item.installmentsCount} {t("common.theMonth")}
                      </div>
                    </Flex>
                    <Flex justify="space-between">
                      <div className="tw:text-color-text-secondary">
                        {t("shop.bnpl.addInstallmentPlan.PlanList.monthlyProfit")}
                      </div>
                      <div className="tw:font-bold tw:text-color-text-secondary">{item.interestRate}٪</div>
                    </Flex>
                    <Flex justify="space-between">
                      <div className="tw:text-color-text-secondary">
                        {t("shop.bnpl.addInstallmentPlan.PlanList.guaranteeType")}
                      </div>
                      <div className="tw:font-bold tw:text-color-text-secondary">
                        {item.guaranteeType === GuaranteeType.Promissory
                          ? t("shop.bnpl.guaranteeTypes.promissory")
                          : null}
                        {item.guaranteeType === GuaranteeType.Salary ? t("shop.bnpl.guaranteeTypes.salary") : null}
                        {item.guaranteeType === GuaranteeType.Cheque ? t("shop.bnpl.guaranteeTypes.cheque") : null}
                        {item.guaranteeType === GuaranteeType.None ? t("shop.bnpl.guaranteeTypes.none") : null}
                      </div>
                    </Flex>
                    <Flex justify="space-between">
                      <div className="tw:text-color-text-secondary">
                        {t("shop.bnpl.addInstallmentPlan.PlanList.code")}
                      </div>
                      <Copy showIcon text={item.code || ""}>
                        <div className="tw:font-bold tw:text-color-text-secondary">{item.code}</div>
                      </Copy>
                    </Flex>
                  </Flex>
                  <Flex gap={"middle"} justify="flex-end" align="center" className="tw:mt-2!">
                    {/* {item.guaranteeType === GuaranteeType.Salary ? (
                      <Button
                        block={false}
                        className="tw:p-0!"
                        type="link"
                        onClick={() => {
                          setOpenDrawer(true);
                          setPlanCode(item.code);
                        }}
                      >
                        {t("shop.bnpl.addInstallmentPlan.PlanList.removeOrAddPeople")}
                      </Button>
                    ) : null} */}
                    <Button
                      block={false}
                      onClick={() => openActivationModal(item)}
                      className="tw:p-0!"
                      loading={activeLoading}
                      type="link"
                    >
                      {item.isActive ? t("common.deactivate") : t("common.activate")}
                    </Button>
                  </Flex>
                </LegacyCard.Ant>
              </Col>
            ))}
        </Row>
      </Flex>

      <Drawer
        open={openDrawer}
        placement={"bottom"}
        size={"100%"}
        title={t("shop.bnpl.InstallmentPlanWithSalary")}
        closeIcon={false}
        zIndex={3005}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              setOpenDrawer(false);
            }}
          />
        }
        classNames={{
          body: "tw:p-0!",
        }}
      >
        <div className="tw:p-10! tw:px-20! tw:max-sm:px-4">
          <EditSalaryDeduction
            planCode={planCode}
            onNext={() => {
              setOpenDrawer(false);
              getList();
            }}
          />
        </div>
      </Drawer>

      <Modal
        open={openModal}
        footer={null}
        onCancel={() => setOpenModal(false)}
        closable={true}
        className="tw:max-w-100"
      >
        <Flex vertical gap={"middle"}>
          <Flex gap={"middle"} align={"flex-start"}>
            <InfoCircleFilled width={24} className="tw:fill-color-warning" />
            <Flex vertical gap={"small"} className="tw:w-full">
              <Typography.Title level={5} className="tw:m-0!">
                {t("shop.bnpl.addInstallmentPlan.PlanList.deactiveConfirmTitle")}
              </Typography.Title>
              <Typography.Text>{t("shop.bnpl.addInstallmentPlan.PlanList.deactiveConfirmDescription")}</Typography.Text>
            </Flex>
          </Flex>
          <Flex className="tw:w-full" justify="end">
            <Button loading={activeLoading} type="primary" onClick={() => toggleActive(activePlan)}>
              {t("shop.bnpl.addInstallmentPlan.steps.contract.form.salary.modal.submit")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default BnplManagePlanList;
